import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const upsertInProgressRef = useRef(false);
  const lastUpsertedUserRef = useRef(null);

  // Upsert user into public.users table when they sign in
  const upsertUser = useCallback(async (authUser) => {
    if (!authUser || !supabase) return;

    // Prevent duplicate upserts for the same user
    if (upsertInProgressRef.current || lastUpsertedUserRef.current === authUser.id) {
      return;
    }

    upsertInProgressRef.current = true;

    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: authUser.id,
          email: authUser.email,
          username: authUser.user_metadata?.user_name ||
                   authUser.user_metadata?.preferred_username ||
                   authUser.user_metadata?.full_name ||
                   authUser.email?.split('@')[0],
          role: 'both',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error upserting user to users table:', error);
      } else {
        console.log('User successfully synced to users table:', authUser.id);
        lastUpsertedUserRef.current = authUser.id;
      }
    } catch (err) {
      console.error('Failed to upsert user:', err);
    } finally {
      upsertInProgressRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Upsert user if session exists
      if (session?.user) {
        upsertUser(session.user);

        // Clean up OAuth tokens from URL hash after successful sign-in
        if (window.location.hash && window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Upsert user when auth state changes (sign in, token refresh, etc.)
      if (session?.user) {
        await upsertUser(session.user);

        // Clean up OAuth tokens from URL hash after successful sign-in
        if (window.location.hash && window.location.hash.includes('access_token')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [upsertUser]);

  const signInWithGitHub = async () => {
    if (!supabase) {
      return { data: null, error: new Error('Supabase is not configured') };
    }

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/`,
          // Request repo scope to allow reading and writing to repositories
          scopes: 'repo read:user user:email',
        },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error signing in with GitHub:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    if (!supabase) {
      return { error: new Error('Supabase is not configured') };
    }

    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    loading,
    signInWithGitHub,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
